import { useEffect, useState, useRef } from "react";
import WebService from "../../Services/WebService";
import { useNavigate } from "react-router-dom";
import { LoungeAttributes } from "../../components/Common/BackendAttributes/BackendAttributes";
import { toast } from "react-toastify";
import MobileLoader from "../../components/MobileLoader/MobileLoader";
import { getEncryptedSessionData, setEncryptedSessionData } from "../../Services/SessionStorageService";
import QrOrderRealtimeService, { QrOrderPayload } from "../../Services/QrOrderRealtimeService";
import "./OrderView.scss";


export interface PosOrderData {
    menus: any[];
    timestamp: string;
    type: "POS_ORDER" | "QR_ORDER";
    loungeId?: string | number;
    tokenNumber?: string | number;
    isNew: boolean;
    serverTime?: string;
    fetchedAt?: number;
}

export const getPosOrderCacheKey = (tenantId: string) => `pos_orders_by_table_${tenantId}`;

const TableView = () => {
    const navigate = useNavigate();
    const [isLoading, setLoading] = useState(false);
    const [loungeList, setLoungeList] = useState<LoungeAttributes[]>([]);
    const [keyword, setKeyword] = useState("");
    const tenantId = localStorage.getItem("tenantId") || "default";
    const loungeCacheKey = `table_view_lounge_list_${tenantId}`;
    const qrOrderCacheKey = `qr_table_orders_${tenantId}`;
    const posOrderCacheKey = getPosOrderCacheKey(tenantId);
    const [qrOrdersByTable, setQrOrdersByTable] = useState<Record<string, QrOrderPayload>>({});
    const [posOrdersByTable, setPosOrdersByTable] = useState<Record<string, PosOrderData>>({});
    const originalLoungeList = useRef<LoungeAttributes[]>([]);

    useEffect(() => {
        getLoungeList();
        setQrOrdersByTable(getEncryptedSessionData<Record<string, QrOrderPayload>>(qrOrderCacheKey) || {});
        const savedPosOrders = getEncryptedSessionData<Record<string, PosOrderData>>(posOrderCacheKey) || {};
        const restored = Object.fromEntries(
            Object.entries(savedPosOrders).map(([k, v]) => [k, { ...v, isNew: false }])
        );
        setPosOrdersByTable(restored);
    }, []);

    const updatePosOrders = (updater: (prev: Record<string, PosOrderData>) => Record<string, PosOrderData>) => {
        setPosOrdersByTable((prev) => {
            const next = updater(prev);
            setEncryptedSessionData(posOrderCacheKey, next);
            return next;
        });
    };

    useEffect(() => {
        const unsubscribe = QrOrderRealtimeService.subscribe((order: QrOrderPayload) => {
            // ── Tenant ID check ──
            const orderTenantId = String(order.tenantId || order.tenant_id || "");
            const localTenantId = localStorage.getItem("tenantId") || "default";
            if (orderTenantId && orderTenantId !== localTenantId) {
                console.log("Tenant ID mismatch, ignoring order event:", orderTenantId, "expected:", localTenantId);
                return;
            }

            const tableNo = normalizeTableNo(order.tableNo || order.table_no);
            if (!tableNo) return;
            const action = order.action || "ADD";
            const incomingType = order.type || "";
            // CLEAR_TABLE — table bilkul hatao
            if (action === "CLEAR_TABLE") {
                setQrOrdersByTable((prev) => {
                    const next = { ...prev };
                    delete next[tableNo];
                    setEncryptedSessionData(qrOrderCacheKey, next);
                    return next;
                });
                updatePosOrders((prev) => {
                    const next = { ...prev };
                    delete next[tableNo];
                    setEncryptedSessionData(posOrderCacheKey, next);
                    return next;
                });
                return;
            }

            if (incomingType === "POS_ORDER") {
                const freshMenus = order.menus || [];
                if (freshMenus.length === 0) {
                    setQrOrdersByTable((prev) => {
                        const next = { ...prev };
                        delete next[tableNo];
                        setEncryptedSessionData(qrOrderCacheKey, next);
                        return next;
                    });
                    updatePosOrders((prev) => {
                        const next = { ...prev };
                        delete next[tableNo];
                        setEncryptedSessionData(posOrderCacheKey, next);
                        return next;
                    });
                } else {
                    updatePosOrders((prev) => ({
                        ...prev,
                        [tableNo]: {
                            menus: freshMenus,
                            timestamp: order.timestamp || new Date().toISOString(),
                            type: "POS_ORDER",
                            loungeId: order.loungeId || order.lounge_id,
                            tokenNumber: order.tokenNumber || order.token_number,
                            isNew: true,
                            serverTime: order.serverTime || prev[tableNo]?.serverTime,
                            fetchedAt: Date.now(),
                        },
                    }));
                    setTimeout(() => {
                        updatePosOrders((prev) => {
                            if (!prev[tableNo]) return prev;
                            return { ...prev, [tableNo]: { ...prev[tableNo], isNew: false } };
                        });
                    }, 5000);
                }
                return;
            }

            // QR_ORDER
            if (incomingType === "QR_ORDER") {
                setQrOrdersByTable((prev) => {
                    const next = { ...prev, [tableNo]: order };
                    setEncryptedSessionData(qrOrderCacheKey, next);
                    return next;
                });
                updatePosOrders((prev) => ({
                    ...prev,
                    [tableNo]: {
                        menus: order.menus || [],
                        timestamp: order.timestamp || new Date().toISOString(),
                        type: "QR_ORDER",
                        loungeId: order.loungeId || order.lounge_id,
                        tokenNumber: order.tokenNumber || order.token_number,
                        isNew: true,
                        serverTime: order.serverTime || prev[tableNo]?.serverTime,
                        fetchedAt: Date.now(),
                    },
                }));
                setTimeout(() => {
                    updatePosOrders((prev) => {
                        if (!prev[tableNo]) return prev;
                        return { ...prev, [tableNo]: { ...prev[tableNo], isNew: false } };
                    });
                }, 5000);
                return;
            }

            // Baaki saare events (add_menu_items_on_table etc.) —
            // menus nahi aate, existing data preserve karo
            updatePosOrders((prev) => {
                const existing = prev[tableNo];
                if (!existing) return prev;
                return { ...prev, [tableNo]: { ...existing, isNew: false } };
            });
        });
        return unsubscribe;
    }, [qrOrderCacheKey, posOrderCacheKey]);


    const normalizeTableNo = (value: any) => String(value || "").trim();

    const parseLoungeTables = (tables: any): any[] => {
        if (Array.isArray(tables)) return tables;
        try {
            const parsed = JSON.parse(tables);
            return Array.isArray(parsed) ? parsed : [];
        } catch { return []; }
    };

    const getTableNumber = (table: any) => {
        if (typeof table === "object" && table !== null) {
            return normalizeTableNo(table.table_no || table.tableNo || table.id);
        }
        return normalizeTableNo(table);
    };


    const openTableOrder = (tableNo: string, loungeId: number | string) => {
        const posData = posOrdersByTable[tableNo];
        const qrOrder = qrOrdersByTable[tableNo];
        const params = new URLSearchParams({ loungeId: String(loungeId) });

        if (posData && posData.menus.length > 0) {
            params.set("qrOrder", "1");
            const normalizedMenus = posData.menus.map((item: any, idx: number) => ({
                ...item,
                menu_id: item.menu_id || item.id || item.item_id || `item_${idx}`,
                id: item.id || item.menu_id || item.item_id || `item_${idx}`,
                name: item.name || item.item_name || item.menu_name || "",
                gross_price: Number(item.gross_price ?? item.price ?? item.unit_price ?? 0),
                quantity: Number(item.quantity || 1),
            }));
            const posAsQrOrder: QrOrderPayload = {
                tableNo,
                table_no: tableNo,
                loungeId,
                lounge_id: loungeId,
                menus: normalizedMenus,
                type: posData.type,
                tokenNumber: posData.tokenNumber,
                token_number: posData.tokenNumber,
                timestamp: posData.timestamp,
            };
            navigate(`/order-view/${tableNo}?${params.toString()}`, { state: { qrOrder: posAsQrOrder } });

        } else if (qrOrder) {
            params.set("qrOrder", "1");
            if (qrOrder.orderId) params.set("orderId", String(qrOrder.orderId));
            navigate(`/order-view/${tableNo}?${params.toString()}`, { state: { qrOrder } });

        } else {
            navigate(`/order-view/${tableNo}?${params.toString()}`);
        }
    };

    const getLoungeList = () => {
        const cached = getEncryptedSessionData<LoungeAttributes[]>(loungeCacheKey);
        if (cached) {
            setLoungeList(cached);
            originalLoungeList.current = cached;
            return;
        }
        setLoading(true);
        WebService.getAPI({ action: "/get-all-lounge-list", body: {} })
            .then((response: any) => {
                const lounges = response?.lounges || [];
                setLoungeList(lounges);
                originalLoungeList.current = lounges;
                setEncryptedSessionData(loungeCacheKey, lounges);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    const filterTableNo = (value: string) => {
        setKeyword(value);
        if (value.trim() === "") { setLoungeList(originalLoungeList.current); return; }
        let found = false;
        const filtered = originalLoungeList.current
            .map((lounge) => {
                const tables = parseLoungeTables(lounge.lounge_tables);
                const ft = tables.filter((t: any) => {
                    const no = typeof t === "string" ? t : t.table_no || t.tableNo || "";
                    return no.toLowerCase().includes(value.toLowerCase());
                });
                if (ft.length > 0) found = true;
                return { ...lounge, lounge_tables: JSON.stringify(ft) };
            })
            .filter((l) => parseLoungeTables(l.lounge_tables).length > 0);
        if (!found) { toast.error("Table not found"); setLoungeList(originalLoungeList.current); }
        else setLoungeList(filtered);
    };

    return (
        <>
            <style>{`
                @keyframes tv-pulse {
                    0%   { box-shadow: 0 0 0 0 rgba(254,190,16,0.9); }
                    70%  { box-shadow: 0 0 0 10px rgba(254,190,16,0); }
                    100% { box-shadow: 0 0 0 0 rgba(254,190,16,0); }
                }
                .tv-pulse-anim { animation: tv-pulse 1s ease-out infinite; }
            `}</style>

            <div className="bg-white mt-1 p-2 rounded-1 font-12">
                <MobileLoader show={isLoading} />
                {loungeList && loungeList.length > 0 && loungeList.map((lounge: any) => (
                    <div key={lounge.id} className="mb-0">
                        <div className="d-flex w-100">
                            <div className="divider" style={{ width: "100%" }}>{lounge.name}</div>
                        </div>
                        <div className="font-12 fw-bold d-flex align-items-center pt-2 mb-1" />
                        Table No. &nbsp;
                        <input
                            type="text"
                            className="form-control form-control-sm mb-2"
                            name="name"
                            placeholder="Search table number"
                            value={keyword}
                            style={{ width: "100%", maxWidth: 250, minWidth: 120 }}
                            onChange={(e) => filterTableNo(e.target.value)}
                        />
                        <div className="d-flex flex-wrap p-1">
                            {parseLoungeTables(lounge.lounge_tables).map((table: any, index: number) => {
                                const tableNo = getTableNumber(table);
                                const posData = posOrdersByTable[tableNo];
                                const hasOrder = Boolean(posData);
                                const isNew = Boolean(posData?.isNew);
                                return (
                                    <div key={`${tableNo}-${index}`} className={`table-box me-2 mb-2 cursor-pointer ${hasOrder ? "qr-order-highlight" : ""} ${isNew ? "tv-pulse-anim" : ""}`}
                                        onClick={() => openTableOrder(tableNo, lounge.id)}
                                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                                    >
                                        <div className="table-status" style={{ background: "#FEBE10" }} />
                                        <div className="table-number"><span>{tableNo}</span></div>
                                        {/* Occupied/Available status below table */}

                                        {hasOrder ? (
                                            <div className="occupied-status">
                                                Occupied
                                            </div>
                                        ) : (
                                            <div className="available-status">
                                                Available
                                            </div>
                                        )}

                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

export default TableView;
